/**
 * Skeleton loader for fixture details view - matches the modern glassmorphism design
 */
export function FixtureDetailsSkeleton() {
  return (
    <div className="fixture-details-page px-4 md:px-0">
      {/* Hero Section Skeleton */}
      <div className="mb-4 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-500 p-6 shadow-xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              {/* Ship icon placeholder */}
              <div className="h-8 w-8 animate-pulse rounded bg-white/20 dark:bg-white/10" />
              {/* Title placeholder */}
              <div className="h-8 w-64 animate-pulse rounded bg-white/20 dark:bg-white/10" />
              {/* Badge placeholder */}
              <div className="h-6 w-16 animate-pulse rounded-full bg-white/20" />
              {/* Vessel badge placeholders */}
              <div className="h-6 w-6 animate-pulse rounded-full bg-white/20" />
              <div className="h-6 w-6 animate-pulse rounded-full bg-white/20" />
            </div>

            <div className="mb-2 flex flex-wrap gap-4 text-sm">
              <div className="h-4 w-32 animate-pulse rounded bg-white/20 dark:bg-white/10" />
              <div className="h-4 w-24 animate-pulse rounded bg-white/20 dark:bg-white/10" />
              <div className="h-4 w-28 animate-pulse rounded bg-white/20 dark:bg-white/10" />
            </div>

            <div className="h-3 w-56 animate-pulse rounded bg-blue-100/30" />
          </div>
        </div>
      </div>

      {/* Key Metrics Cards Skeleton */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="rounded-xl border border-gray-200 bg-white/5 p-4 backdrop-blur dark:border-slate-700/50 dark:bg-slate-800/50"
          >
            <div className="mb-2 h-3 w-16 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
            <div className="mb-1 h-8 w-20 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
            <div className="h-4 w-12 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
          </div>
        ))}
      </div>

      {/* Main Content Grid Skeleton */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Main Content (2 columns) */}
        <div className="space-y-4 md:col-span-2">
          {/* Charter Details Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-5 w-5 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
              <div className="h-6 w-32 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
            </div>
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex justify-between border-b border-gray-100 py-2 dark:border-slate-700/50"
                >
                  <div className="h-4 w-24 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
                  <div className="h-4 w-32 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
                </div>
              ))}
            </div>
          </div>

          {/* Financial Details & Performance Cards */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Financial Details */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <div className="mb-4 flex items-center gap-2">
                <div className="h-5 w-5 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
                <div className="h-6 w-32 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <div className="mb-1 h-3 w-20 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
                    <div className="h-5 w-24 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Card */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <div className="mb-4 flex items-center gap-2">
                <div className="h-5 w-5 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
                <div className="h-6 w-32 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <div className="mb-1 h-3 w-20 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
                    <div className="h-5 w-24 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Speed & Consumption Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-5 w-5 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
              <div className="h-6 w-40 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[1, 2].map((i) => (
                <div key={i}>
                  <div className="mb-3 h-4 w-16 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
                  <div className="space-y-2">
                    <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
                    <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar (1 column) */}
        <div className="space-y-4">
          {/* Quick Stats Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-5 w-5 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
              <div className="h-6 w-24 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="mb-1 h-3 w-16 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
                  <div className="h-5 w-20 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
                </div>
              ))}
            </div>
          </div>

          {/* Broker Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-5 w-5 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
              <div className="h-6 w-16 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
            </div>
            <div className="h-4 w-32 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
          </div>

          {/* Notes Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-5 w-5 animate-pulse rounded bg-gray-300 dark:bg-slate-600" />
              <div className="h-6 w-16 animate-pulse rounded bg-gray-400 dark:bg-slate-500" />
            </div>
            <div className="space-y-2">
              <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
              <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
              <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
